import { useState, useCallback } from 'react';
import { useToast } from '@/app/providers/ToastProvider';
import { MONTHS } from '@/shared/utils/dateHelpers';
import { budgetService } from '../services/budgetService';

export function useBudgets({ householdId, data, budgets, expenseMap, selMonth, selYear }) {
  const toast = useToast();
  const [copyBudgetOpen, setCopyBudgetOpen] = useState(false);
  const [copySource, setCopySource] = useState("");
  const [copyMode, setCopyMode] = useState("entire");
  const [copyCategory, setCopyCategory] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const budgetKey = selYear + "-" + (selMonth + 1);
  const currentBudgets = budgets[budgetKey] || {};

  const availableBudgetMonths = Object.entries(budgets)
    .filter(([key, val]) => key !== budgetKey && Object.keys(val || {}).length > 0)
    .map(([key]) => {
      const [y, m] = key.split("-").map(Number);
      return { key, label: MONTHS[m - 1] + " " + y, year: y, month: m };
    })
    .sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month));

  const getMonthsForCategory = useCallback((group) => {
    const items = expenseMap[group] || [];
    return availableBudgetMonths.filter(m => {
      const src = budgets[m.key] || {};
      return items.some(cat => Number(src[cat]) > 0);
    });
  }, [expenseMap, availableBudgetMonths, budgets]);

  const categoryMonths = copyCategory ? getMonthsForCategory(copyCategory) : [];

  const saveBudget = useCallback((cat, value) => {
    budgetService.saveBudget(householdId, data, budgetKey, currentBudgets, cat, value);
    toast.success('Budget updated');
  }, [householdId, data, budgetKey, currentBudgets, toast]);

  const copyBudgetFrom = useCallback((sourceKey) => {
    if (copyMode === "entire") {
      budgetService.copyBudgetEntire(householdId, data, budgetKey, sourceKey);
    } else if (copyMode === "category" && copyCategory) {
      const items = expenseMap[copyCategory] || [];
      budgetService.copyBudgetCategory(householdId, data, budgetKey, sourceKey, currentBudgets, items);
    }
    setCopyBudgetOpen(false);
    setCopySource("");
    setCopyMode("entire");
    setCopyCategory("");
    toast.success('Budget copied');
  }, [householdId, data, budgetKey, copyMode, copyCategory, expenseMap, currentBudgets, toast]);

  const totalBudgeted = Object.values(currentBudgets).reduce((s, v) => s + (Number(v) || 0), 0);

  return {
    budgetKey, currentBudgets, totalBudgeted,
    availableBudgetMonths, categoryMonths,
    copyBudgetOpen, setCopyBudgetOpen,
    copySource, setCopySource,
    copyMode, setCopyMode,
    copyCategory, setCopyCategory,
    deleteConfirm, setDeleteConfirm,
    saveBudget, copyBudgetFrom,
  };
}
